import { useState } from "react";
import { openWhatsApp } from "../lib/whatsapp";
import { BookMeetingButton, PersonMeetings } from "./Appointments";
import { PersonTasks } from "./Tasks";
import { BackBurnerButton, InsuredUnder } from "./Family";
import { TravelPanel } from "./TravelPanel";
import { PersonConsent } from "./Consent";
import { MembershipDate } from "./Membership";
import { PersonType } from "./PersonType";
import { ProvinceField } from "./Province";
import dayjs from "dayjs";
import { db, eq, ilike } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { navigate, setQuery, useRoute } from "../lib/router";
import { AREA_LABEL, useRefData, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { useLog } from "./QuickLog";
import { useAddToPipeline } from "./OppSheet";
import { LEAD_AREAS } from "../lib/refdata";
import { Drawer, Empty, ErrorBox, Field, Loading, Select, Temperature, WorkspaceBadges, daysAgo, fmtDate, fmtShort, useToast } from "./ui";
import { IconMail, IconPhone, IconChat, IconX, IconCake } from "./icons";
import { RemindMeButton } from "./Bell";

/** Opens when the URL has ?person=<id>, from any page */
export function PersonDrawerHost({ workspace }: { workspace: "leads" | "team" | "clients" | "settings" }) {
  const { query } = useRoute();
  const id = query.get("person");
  if (!id) return null;
  return <PersonDrawer key={id} id={id} workspace={workspace} onClose={() => setQuery({ person: null, tab: null })} />;
}

export const openPerson = (id: string) => setQuery({ person: id, tab: null }, { replace: false });

type Person = {
  id: string; first_name: string; last_name: string; preferred_name: string | null; full_name: string; gender?: string | null; known_as?: string | null;
  phone: string | null; email: string | null; birth_month: number | null; birth_day: number | null;
  city: string | null; province: string | null; country: string | null; occupation: string | null; company: string | null;
  linkedin: string | null; instagram: string | null; facebook: string | null; family_note: string | null;
  interests: string | null; ask_about: string | null; notes: string | null; is_client: boolean; client_since: string | null;
  source: string | null; owner_label: string | null;
};

function PersonDrawer({ id, workspace, onClose }: { id: string; workspace: string; onClose: () => void }) {
  const { can } = useSession();
  const { query } = useRoute();
  const log = useLog();
  const addToPipeline = useAddToPipeline();
  const tab = query.get("tab") || "overview";
  const p = useQuery(`person:${id}`, async () => {
    const [person, ws, areas, member, tags] = await Promise.all([
      db.one<Person>("people", { id: eq(id), select: "*" }),
      db.one<{ is_team: boolean; is_lead: boolean; is_client: boolean }>("person_workspaces", { person_id: eq(id) }),
      can("leads") ? db.select("person_area_stats", { person_id: eq(id), select: "*" }) : Promise.resolve([]),
      can("team") ? db.one("team_overview", { person_id: eq(id), select: "*" }) : Promise.resolve(null),
      db.select<{ tag_id: string; tags: { name: string } }>("person_tags", { person_id: eq(id), select: "tag_id,tags(name)" }),
    ]);
    return { person, ws, areas: areas as any[], member: member as any, tags };
  });

  if (p.loading && !p.data) return <Drawer onClose={onClose} label="Person"><Loading rows={8} /></Drawer>;
  if (p.error || !p.data?.person) {
    return (
      <Drawer onClose={onClose} label="Person">
        <div className="drawer-body"><Empty title="This person isn't available">They may have been removed, or you don't have access.</Empty></div>
      </Drawer>
    );
  }
  const { person, ws, areas, member, tags } = p.data;
  const bday = person.birth_month ? dayjs().month(person.birth_month - 1).date(person.birth_day!).format("MMMM D") : null;
  const tabs = [
    ["overview", "Overview"],
    ...(can("leads") && areas.length ? [["lead", "Lead"]] : []),
    ...(can("leads") && areas.some((a) => a.area === "travel") ? [["travel", "Travel"]] : []),
    ...(member ? [["team", "Team"]] : []),
    ["history", "History"],
    ["family", "Family & links"],
    ...(can("sensitive") ? [["private", "Private"]] : []),
  ] as [string, string][];
  const firstArea = (areas[0]?.area as Area) ?? (member ? "team" : "warm");

  return (
    <Drawer onClose={onClose} label={person.full_name}>
      <div className="drawer-head">
        <div className="person-title">
          <div>
            <h2>{person.full_name}{person.known_as && <span className="known-as"> · {person.known_as}</span>}</h2>
            <div className="badges">
              <WorkspaceBadges team={ws?.is_team} lead={ws?.is_lead} client={ws?.is_client} />
              {tags.map((t) => <span key={t.tag_id} className="pill">{t.tags.name}</span>)}
              {bday && <span className="pill special"><IconCake size={13} />{bday}</span>}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><IconX /></button>
        </div>
        <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
          {can("activities", "edit") && (
            <button className="btn primary" onClick={() => log({ person_id: person.id, person_name: person.full_name, area: workspace === "team" && member ? "team" : workspace === "clients" && person.is_client ? "clients" : firstArea })}>Log activity</button>
          )}
          {can("pipelines", "edit") && (
            <button className="btn" onClick={() => addToPipeline({ person_id: person.id, full_name: person.full_name, source_area: LEAD_AREAS.includes(firstArea) ? firstArea : "warm" })}>Add to pipeline</button>
          )}
          <BookMeetingButton person={{ id: person.id, full_name: person.full_name }} />
          {can("activities", "edit") && <BackBurnerButton person={{ id: person.id, full_name: person.full_name }} until={(person as any).back_burner_until} reason={(person as any).back_burner_reason} />}
          {(ws?.is_client || person.is_client) && can("clients") && (
            <button className="btn" title="Their policies and accounts" onClick={() => { onClose(); navigate(`/clients/${person.id}?tab=solutions`); }}>Solutions</button>
          )}
          <RemindMeButton person={{ id: person.id, full_name: person.full_name }} />
          {person.phone && <a className="btn" href={`tel:${person.phone}`}><IconPhone size={16} />Call</a>}
          {person.phone && <button className="btn" onClick={() => openWhatsApp(person.phone)}><IconChat size={16} />WhatsApp</button>}
          {person.email && <a className="btn" href={`mailto:${person.email}`}><IconMail size={16} />Email</a>}
        </div>
        <div className="tabs" role="tablist">
          {tabs.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setQuery({ tab: k })}>{l}</button>
          ))}
        </div>
      </div>
      <div className="drawer-body">
        {tab === "overview" && <>
          <PersonType person={{ id: person.id, full_name: person.full_name }} ws={ws} areas={areas} member={member} tags={tags} onClose={onClose} />
          {(person as any).back_burner_until > dayjs().format("YYYY-MM-DD") && <div className="note-strip">On the back burner until <b>{dayjs((person as any).back_burner_until).format("MMM D, YYYY")}</b>{(person as any).back_burner_reason ? `: ${(person as any).back_burner_reason}` : ""}</div>}
          <Applications personId={person.id} />
          <InsuredUnder personId={person.id} />
          <Overview key={person.id} person={person} onSaved={() => invalidate(`person:${id}`)} />
          <PersonTasks person={{ id: person.id, full_name: person.full_name }} />
          <PersonMeetings person={{ id: person.id, full_name: person.full_name }} />
          <PersonConsent person={{ id: person.id, full_name: person.full_name }} />
        </>}
        {tab === "travel" && <TravelPanel personId={id} />}
        {tab === "lead" && <LeadTab areas={areas} person={person} />}
        {tab === "team" && member && <TeamTab m={member} />}
        {tab === "history" && <History personId={id} workspace={workspace} />}
        {tab === "family" && <Family personId={id} />}
        {tab === "private" && <PrivateTab personId={id} birthday={bday} />}
      </div>
    </Drawer>
  );
}

function Overview({ person, onSaved }: { person: Person; onSaved: () => void }) {
  const { can } = useSession();
  const ref = useRefData();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(person);
  const [error, setError] = useState<Error | null>(null);
  const toast = useToast();
  const editable = can("leads", "edit") || can("team", "edit") || can("clients", "edit");
  const set = (k: keyof Person) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value || null }));

  async function save() {
    setError(null);
    try {
      const { id, full_name, ...rest } = f as any;
      void full_name;
      await db.update("people", { id: eq(id) }, {
        first_name: rest.first_name ?? "", last_name: rest.last_name ?? "", preferred_name: rest.preferred_name,
        phone: rest.phone, email: rest.email, city: rest.city, province: rest.province, country: rest.country,
        occupation: rest.occupation, company: rest.company, linkedin: rest.linkedin, instagram: rest.instagram,
        facebook: rest.facebook, family_note: rest.family_note, interests: rest.interests, ask_about: rest.ask_about,
        notes: rest.notes, gender: rest.gender ?? null, known_as: rest.known_as || null, birth_month: rest.birth_month ? Number(rest.birth_month) : null,
        birth_day: rest.birth_month ? Number(rest.birth_day) || null : null,
      });
      setEditing(false);
      toast("Saved");
      onSaved();
    } catch (e) {
      setError(e as Error);
    }
  }

  if (editing) {
    return (
      <>
        <ErrorBox error={error} />
        <div className="form-grid">
          <Field label="First name"><input className="input" value={f.first_name ?? ""} onChange={set("first_name")} /></Field>
          <Field label="Last name"><input className="input" value={f.last_name ?? ""} onChange={set("last_name")} /></Field>
          <Field label="Goes by (replaces first name)"><input className="input" value={f.preferred_name ?? ""} onChange={set("preferred_name")} placeholder="e.g. Tolu for Tolulope" /></Field>
          <Field label="Known as (to tell them apart)"><input className="input" value={f.known_as ?? ""} onChange={set("known_as")} placeholder="e.g. Church choir, Tunde's sister" maxLength={60} /></Field>
          <Field label="Gender"><Select value={f.gender} onChange={(v) => setF((x) => ({ ...x, gender: v || null }))} options={ref.data?.list("Gender") ?? ["Female", "Male", "Prefer to self-describe", "Prefer not to say"]} /></Field>
          <Field label="Phone"><input className="input" value={f.phone ?? ""} onChange={set("phone")} /></Field>
          <Field label="Email"><input className="input" type="email" value={f.email ?? ""} onChange={set("email")} /></Field>
          <Field label="Birthday month">
            <Select value={f.birth_month?.toString()} onChange={(v) => setF((x) => ({ ...x, birth_month: v ? Number(v) : null }))}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: dayjs().month(i).format("MMMM") }))} />
          </Field>
          <Field label="Birthday day"><input className="input" type="number" min={1} max={31} value={f.birth_day ?? ""} onChange={(e) => setF((x) => ({ ...x, birth_day: e.target.value ? Number(e.target.value) : null }))} /></Field>
          <Field label="City"><input className="input" value={f.city ?? ""} onChange={set("city")} /></Field>
          <ProvinceField province={f.province} country={f.country} onChange={(province, country) => setF((x) => ({ ...x, province, country }))} />
          <Field label="Work"><input className="input" value={f.occupation ?? ""} onChange={set("occupation")} /></Field>
          <Field label="Company"><input className="input" value={f.company ?? ""} onChange={set("company")} /></Field>
          <Field label="LinkedIn"><input className="input" value={f.linkedin ?? ""} onChange={set("linkedin")} /></Field>
          <Field label="Instagram"><input className="input" value={f.instagram ?? ""} onChange={set("instagram")} /></Field>
          <Field label="Family" wide><input className="input" value={f.family_note ?? ""} onChange={set("family_note")} /></Field>
          <Field label="Interests" wide><input className="input" value={f.interests ?? ""} onChange={set("interests")} /></Field>
          <Field label="Next time, ask about…" wide><input className="input" value={f.ask_about ?? ""} onChange={set("ask_about")} /></Field>
          <Field label="Notes" wide><textarea className="textarea" value={f.notes ?? ""} onChange={set("notes")} /></Field>
        </div>
        <div className="toolbar"><button className="btn primary" onClick={save}>Save changes</button><button className="btn ghost" onClick={() => { setF(person); setEditing(false); }}>Cancel</button></div>
      </>
    );
  }
  const row = (k: string, v: React.ReactNode) => (v ? <><dt>{k}</dt><dd>{v}</dd></> : null);
  return (
    <>
      {(person.ask_about || person.family_note || person.interests) && (
        <div className="panel" style={{ padding: 14, background: "var(--accent-soft)", borderColor: "transparent" }}>
          <dl className="kv">
            {row("Ask about", person.ask_about)}
            {row("Family", person.family_note)}
            {row("Interests", person.interests)}
          </dl>
        </div>
      )}
      <dl className="kv">
        {row("Birthday", person.birth_month ? dayjs().month(person.birth_month - 1).date(person.birth_day ?? 1).format("MMMM D") : null)}
        {row("Gender", person.gender)}
        {row("Phone", person.phone)}
        {row("Email", person.email)}
        {row("Lives in", [person.city, person.province, person.country].filter(Boolean).join(", "))}
        {row("Work", [person.occupation, person.company].filter(Boolean).join(" at "))}
        {row("LinkedIn", person.linkedin && <a href={person.linkedin} target="_blank" rel="noreferrer">Profile</a>)}
        {row("Instagram", person.instagram)}
        {row("Source", person.source)}
        {row("Client since", person.client_since && fmtDate(person.client_since))}
        {row("Notes", person.notes)}
      </dl>
      {editable && <div><button className="btn" onClick={() => setEditing(true)}>Edit details</button></div>}
    </>
  );
}

function LeadTab({ areas, person }: { areas: any[]; person: any }) {
  const { can } = useSession();
  const toast = useToast();
  const addToPipeline = useAddToPipeline();
  const editable = can("leads", "edit");
  async function setOutreach(a: any, on: boolean) {
    await db.update("person_areas", { id: eq(a.person_area_id) }, { outreach_since: on ? dayjs().format("YYYY-MM-DD") : null });
    invalidate(`person:${person.id}`); invalidate("people"); invalidate("today"); invalidate("dash:");
    toast(on ? `${person.full_name} moved to Outreach` : `${person.full_name} is back in the warm market`);
  }
  return (
    <>
      {areas.map((a) => (
        <div key={a.person_area_id} className="panel" style={{ padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong>{a.area === "warm" && a.outreach_since ? "Outreach (warm market)" : AREA_LABEL[a.area as Area]}</strong>
            <Temperature t={a.temperature} />
          </div>
          <dl className="kv">
            <dt>Status</dt><dd>{a.status}{a.bond ? ` · ${a.bond}` : ""}</dd>
            <dt>Last touch</dt><dd>{daysAgo(a.last_touch)}</dd>
            <dt>Last two-way</dt><dd>{daysAgo(a.last_two_way)}</dd>
            {a.in_pipeline ? <>
              <dt>Next move</dt><dd><b>In the pipeline:</b> follow the deal's next action. The give-first rhythm is paused while the deal is open.</dd>
            </> : <>
              <dt>Next check-in</dt><dd>{a.next_check_in ? fmtShort(a.next_check_in) : "—"}{a.rhythm_days ? ` (every ${a.rhythm_days} days)` : ""}</dd>
              <dt>Next move</dt><dd>{a.next_move}</dd>
            </>}
            {a.space_rule !== "OK" && <><dt>Space</dt><dd>{a.space_rule}</dd></>}
            {a.rule_48h && <><dt>48-hour rule</dt><dd>{a.rule_48h}</dd></>}
            {a.relationship && <><dt>Relationship</dt><dd>{a.relationship}</dd></>}
            {a.interest && <><dt>Interest</dt><dd>{a.interest}</dd></>}
            {a.segment && <><dt>Segment</dt><dd>{a.segment} · {a.wealth_tier}</dd></>}
            {a.next_step && <><dt>Next step</dt><dd>{a.next_step}</dd></>}
          </dl>
          {editable && (
            <div className="toolbar" style={{ margin: "12px 0 0" }}>
              {a.area === "warm" && !a.outreach_since && <button className="btn small" onClick={() => setOutreach(a, true)}>Reignited · move to Outreach</button>}
              {a.area === "warm" && a.outreach_since && <button className="btn small ghost" onClick={() => setOutreach(a, false)}>Back to warm market</button>}
              {can("pipelines", "edit") && (a.area !== "warm" || a.outreach_since) && (
                <button className="btn small" onClick={() => addToPipeline({ person_id: person.id, full_name: person.full_name, source_area: a.area })}>Moving forward · add to pipeline</button>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function TeamTab({ m }: { m: any }) {
  return (
    <div className="panel" style={{ padding: 14 }}>
      <dl className="kv">
        <dt>Agent code</dt><dd>{m.agent_code ?? "—"}</dd>
        <dt>Status</dt><dd>{m.status}{m.licensed ? " · Licensed" : " · Not licensed"}</dd>
        <dt>Level</dt><dd>{m.level ?? "—"}</dd>
        <dt>Recruiter</dt><dd>{m.recruiter ?? "—"}</dd>
        <dt>Recruited</dt><dd>{fmtDate(m.recruit_date)}</dd>
        {!m.licensed && m.deadline_9m && <><dt>Membership expires</dt><dd><MembershipDate m={m} long /></dd></>}
        <dt>Last check-in</dt><dd>{daysAgo(m.last_contact)}</dd>
        <dt>Direct team</dt><dd>{m.direct_team} · {m.total_downline} in downline</dd>
      </dl>
      <div style={{ marginTop: 10 }}><a className="btn" href={`/team/members/${m.member_id}`}>Open team profile</a></div>
    </div>
  );
}

export function History({ personId, workspace, hideToggle }: { personId: string; workspace: string; hideToggle?: boolean }) {
  const [all, setAll] = useState(false);
  const log = useLog();
  const toast = useToast();
  const { can } = useSession();
  const [confirm, setConfirm] = useState<string | null>(null);
  async function del(id: string) {
    if (confirm !== id) return setConfirm(id);
    await db.remove("activities", { id: eq(id) });
    setConfirm(null); invalidate(`person:${personId}`); invalidate("today"); invalidate("team"); invalidate("cc");
    toast("Entry deleted (kept in the change history)");
  }
  async function commit(id: string, s: string) {
    await db.update("activities", { id: eq(id) }, { commitment_status: s });
    invalidate(`person:${personId}`); invalidate("team"); invalidate("cc"); toast(s === "Done" ? "Marked kept" : "Marked broken");
  }
  const acts = useQuery(`person:${personId}:acts:${all ? "all" : workspace}`, () =>
    db.select("activities", {
      person_id: eq(personId), select: "id,occurred_on,area,workspace,activity_type,channel,what_happened,next_step,follow_up_on,follow_up_done,commitment,commitment_due,commitment_status,spark,topic,contacted_by,person_id,people(full_name)",
      order: "occurred_on.desc,created_at.desc", limit: 200,
      ...(all || workspace === "settings" || workspace === "clients" ? {} : { workspace: eq(workspace) }),
    }));
  return (
    <>
      {!hideToggle && <label className="check"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show activity from all workspaces</label>}
      {acts.loading && !acts.data ? <Loading /> : acts.data?.length ? (
        <ul className="timeline">
          {acts.data.map((a: any) => (
            <li key={a.id}>
              <time>{fmtShort(a.occurred_on)}</time>
              <div>
                <div className="what">{a.activity_type}{a.channel && <span className="muted"> · {a.channel}</span>}</div>
                {a.what_happened && <div className="sub">{a.what_happened}</div>}
                {a.commitment && <div className="sub">Committed: {a.commitment}{a.commitment_due && ` · by ${fmtShort(a.commitment_due)}`}{" "}
                  {a.commitment_status === "Done" ? <span className="pill warm">Kept</span> : a.commitment_status === "Missed" ? <span className="pill alert">Broken</span> : <>
                    <span className="pill">{a.commitment_status ?? "Pending"}</span>
                    {can("activities", "edit") && <><button className="btn small" onClick={() => commit(a.id, "Done")}>Kept</button><button className="btn small ghost" onClick={() => commit(a.id, "Missed")}>Broken</button></>}
                  </>}</div>}
                {a.next_step && <div className="small muted">Next: {a.next_step}{a.follow_up_on && ` · follow up ${fmtShort(a.follow_up_on)}`}{a.follow_up_done && " ✓"}</div>}
                {can("activities", "edit") && <div className="tl-actions">
                  <button className="btn small ghost" onClick={() => log({ edit: a, person_id: a.person_id, person_name: a.people?.full_name, area: a.area })}>Edit</button>
                  <button className={confirm === a.id ? "btn small danger" : "btn small ghost"} onClick={() => del(a.id)}>{confirm === a.id ? "Click again to delete" : "Delete"}</button>
                </div>}
              </div>
            </li>
          ))}
        </ul>
      ) : <Empty title="No activity yet">Log the first touch with the Log button.</Empty>}
    </>
  );
}

function Family({ personId }: { personId: string }) {
  const ref = useRefData();
  const toast = useToast();
  const links = useQuery(`person:${personId}:links`, () =>
    db.select("person_links", { person_id: eq(personId), select: "relationship_id,other_person_id,type_key,label,notes,via_person_id,derived" }));
  const ids = [...new Set((links.data ?? []).flatMap((l: any) => [l.other_person_id, l.via_person_id].filter(Boolean)))];
  const names = useQuery(ids.length ? `person:${personId}:linknames:${ids.join(",")}` : null, () =>
    db.select<{ id: string; full_name: string; birth_month: number | null; birth_day: number | null }>("people", { id: `in.(${ids.join(",")})`, select: "id,full_name,birth_month,birth_day" }));
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState("parent_of");
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const found = useQuery(dq.length >= 2 ? `linkfind:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { full_name: ilike(dq), select: "id,full_name", limit: 8, order: "full_name" }));
  const [newFirst, setNewFirst] = useState("");
  const { can } = useSession();
  const [dob, setDob] = useState("");
  const [noYear, setNoYear] = useState(false);
  const [gender, setGender] = useState("");

  async function link(otherId: string) {
    await db.insert("relationships", { from_person: personId, to_person: otherId, type_key: type });
    // birthday and gender for the person being linked (only what was filled in)
    if (dob) {
      const d = dayjs(dob);
      await db.update("people", { id: eq(otherId) }, { birth_month: d.month() + 1, birth_day: d.date(), ...(gender ? { gender } : {}) });
      if (!noYear && can("sensitive", "edit")) await db.upsert("people_private", { person_id: otherId, date_of_birth: dob }, "person_id").catch(() => undefined);
    } else if (gender) await db.update("people", { id: eq(otherId) }, { gender });
    invalidate(`person:${personId}`);
    invalidate(`person:${otherId}`);
    setAdding(false); setQ(""); setNewFirst(""); setDob(""); setGender(""); setNoYear(false);
    toast("Linked");
  }
  async function createAndLink() {
    const [p] = await db.insert<{ id: string }>("people", { first_name: newFirst.split(" ")[0], last_name: newFirst.split(" ").slice(1).join(" ") });
    await link(p.id);
  }
  const nameOf = (id: string) => names.data?.find((n) => n.id === id);

  return (
    <>
      {links.data?.length ? (
        <ul className="timeline">
          {links.data.map((l: any) => {
            const o = nameOf(l.other_person_id);
            return (
              <li key={l.relationship_id + l.other_person_id}>
                <span className="small muted">{l.derived ? l.label.replace(/ · .*/, "") : l.label}{l.derived && ` · ${nameOf(l.via_person_id)?.full_name.split(" ")[0] ?? "spouse"}'s ${l.type_key === "parent_of" ? "child" : "spouse"}`}</span>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <button className="btn ghost small" style={{ padding: 0 }} onClick={() => openPerson(l.other_person_id)}>
                    <strong>{o?.full_name ?? "…"}</strong>
                  </button>
                  {o?.birth_month && <span className="pill special">{dayjs().month(o.birth_month - 1).date(o.birth_day!).format("MMM D")}</span>}
                  {!l.derived && <button className="btn ghost small danger" onClick={async () => { await db.remove("relationships", { id: eq(l.relationship_id) }); invalidate(`person:${personId}`); }}>Remove</button>}
                </div>
              </li>
            );
          })}
        </ul>
      ) : <Empty title="No family or links yet">Link children, a spouse, who referred them, or who they insured.</Empty>}
      {adding ? (
        <div className="panel" style={{ padding: 14, display: "grid", gap: 10 }}>
          <Field label="This person is…">
            <Select value={type} onChange={setType} options={(ref.data?.relTypes ?? []).map((r) => ({ value: r.key, label: r.label }))} />
          </Field>
          <div className="form-grid">
            <Field label="Their date of birth (optional)"><input type="date" className="input" value={dob} max={dayjs().format("YYYY-MM-DD")} onChange={(e) => setDob(e.target.value)} />
              {dob && <label className="small" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                <input type="checkbox" checked={noYear} onChange={(e) => setNoYear(e.target.checked)} /> I only know the day and month</label>}
              {dob && !noYear && !can("sensitive", "edit") && <span className="small muted">The birthday is saved; the full date needs "Sensitive details" access.</span>}</Field>
            <Field label="Gender (optional)"><Select value={gender} onChange={setGender} options={ref.data?.list("Gender") ?? []} /></Field>
          </div>
          <Field label="…of (search)"><input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name" /></Field>
          {(found.data ?? []).filter((f) => f.id !== personId).map((f) => (
            <button key={f.id} className="btn ghost" style={{ justifyContent: "flex-start" }} onClick={() => link(f.id)}>{f.full_name}</button>
          ))}
          <Field label="Or add someone new (e.g. a child)">
            <div style={{ display: "flex", gap: 8 }}>
              <input className="input" value={newFirst} onChange={(e) => setNewFirst(e.target.value)} placeholder="Full name" />
              <button className="btn" disabled={!newFirst.trim()} onClick={createAndLink}>Add and link</button>
            </div>
          </Field>
          <div><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button></div>
        </div>
      ) : <div><button className="btn" onClick={() => setAdding(true)}>Add a link</button></div>}
    </>
  );
}

function PrivateTab({ personId, birthday }: { personId: string; birthday?: string | null }) {
  const { can } = useSession();
  const toast = useToast();
  const d = useQuery(`person:${personId}:private`, () => db.one("people_private", { person_id: eq(personId), select: "*" }));
  const [f, setF] = useState<{ date_of_birth?: string; address?: string; legal_full_name?: string } | null>(null);
  const v = f ?? d.data ?? {};
  async function save() {
    await db.upsert("people_private", { person_id: personId, date_of_birth: v.date_of_birth || null, address: v.address || null, legal_full_name: v.legal_full_name || null }, "person_id");
    invalidate(`person:${personId}`);
    setF(null);
    toast("Saved");
  }
  return (
    <div className="form-grid">
      <Field label="Date of birth"><input type="date" className="input" disabled={!can("sensitive", "edit")} value={v.date_of_birth ?? ""} onChange={(e) => setF({ ...v, date_of_birth: e.target.value })} />
        {!v.date_of_birth && birthday && <span className="small muted">Birthday on file: {birthday} (year not known). Add the full date when you have it.</span>}</Field>
      <Field label="Legal full name"><input className="input" disabled={!can("sensitive", "edit")} value={v.legal_full_name ?? ""} onChange={(e) => setF({ ...v, legal_full_name: e.target.value })} /></Field>
      <Field label="Address" wide><input className="input" disabled={!can("sensitive", "edit")} value={v.address ?? ""} onChange={(e) => setF({ ...v, address: e.target.value })} /></Field>
      {f && <div className="wide"><button className="btn primary" onClick={save}>Save</button></div>}
    </div>
  );
}

/** Solutions applied for and not in force yet (from pipeline deals, or added by hand). */
function Applications({ personId }: { personId: string }) {
  const q = useQuery(`person:${personId}:apps`, () => db.select<{ id: string; solution_type: string; provider: string; status: string; status_since: string | null; premium: number | null; premium_frequency: string | null }>(
    "client_holdings", { person_id: eq(personId), status: `in.("Submitted","In underwriting","Approved – not placed","Pending")`, select: "id,solution_type,provider,status,status_since,premium,premium_frequency" }));
  if (!q.data?.length) return null;
  return (
    <div className="ptype">
      <div className="sol-head"><strong>Applications in progress</strong><span className="small muted">Not counted until Active</span></div>
      <ul className="timeline">{q.data.map((h) => (
        <li key={h.id}><span className="small muted">{h.status}{h.status_since ? ` since ${dayjs(h.status_since).format("MMM D")}` : ""}</span>
          <div><strong>{h.solution_type}</strong> · {h.provider}{h.premium ? ` · $${h.premium}${h.premium_frequency === "Annual" ? "/yr" : "/mo"}` : ""}</div></li>
      ))}</ul>
    </div>
  );
}
